import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import useHandleAppException, {
  AppExceptionResponse,
} from '../../../../../hooks/useHandleAppException';
import LangEnum from '../../../../../lang/LangEnum';
import { getAuthLoginPath } from '../../../../../routes/auth.routes';
import api from '../../../../../services/api';
import snackbarStore from '../../../../../stores/Snackbar.store';
import { useTranslation } from '../../../../translation/TranslationContext';
import { useAuthContext } from '../../../AuthContext';
import { getRecoverPasswordOutcome } from '../recoverPasswordPanel.utils';
import useRecoverPasswordCooldown from './useRecoverPasswordCooldown';

const TOO_MANY_REQUESTS_STATUS = 429;

const useRecoverPasswordForm = () => {
  const { translate } = useTranslation();
  const { handleErrorResponse } = useHandleAppException();
  const { loginContext } = useAuthContext();
  const { handleMessage } = snackbarStore;

  const schema = z.object({
    email: z
      .string()
      .trim()
      .min(1, translate(LangEnum.GLOBAL_REQUIRED))
      .pipe(z.email(translate(LangEnum.GLOBAL_INVALID_EMAIL))),
  });

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
    mode: 'onBlur',
  });

  const email = useWatch({ control: form.control, name: 'email' });
  const { sentContent, isCoolingDown, resendLabel, registerOutcome } =
    useRecoverPasswordCooldown(email);

  const loginPath = getAuthLoginPath(loginContext?.slug);

  const handleSubmit = form.handleSubmit(async (values) => {
    const response = await api.recoverPassword({ email: values.email });
    const outcome = getRecoverPasswordOutcome(response);

    if (outcome) {
      registerOutcome(values.email, outcome);
      return;
    }

    if (response.status === TOO_MANY_REQUESTS_STATUS) {
      handleMessage({
        type: 'error',
        title: translate(LangEnum.LOGIN_RECOVER_TOO_MANY_ATTEMPTS_TITLE),
        message: translate(LangEnum.LOGIN_RECOVER_TOO_MANY_ATTEMPTS_HINT),
      });
      return;
    }

    const errorMessage =
      (response.data
        ? handleErrorResponse(response as unknown as AppExceptionResponse)
        : undefined) || translate(LangEnum.GLOBAL_INVALID_EMAIL);

    handleMessage({
      type: 'error',
      title: errorMessage,
      message: translate(LangEnum.LOGIN_RECOVER_ERROR_HINT),
    });
    form.setError('email', { message: errorMessage });
  });

  const resendButton = {
    label: resendLabel,
    disabled: isCoolingDown,
    loading: form.formState.isSubmitting,
    onClick: handleSubmit,
  };

  return { form, handleSubmit, loginPath, sentContent, resendButton };
};

export default useRecoverPasswordForm;
